import { API } from "../../api";
import {
  mapStaffRealEstateDetail,
  mapStaffRealEstateListItem,
  mapStaffRealEstateSummary,
} from "../../../utils/staffRealEstateAdapter";

function transformListResponse(responseData) {
  const data = responseData?.data;
  return {
    items: (data?.items || []).map(mapStaffRealEstateListItem).filter(Boolean),
    pagination: data?.pagination || {},
  };
}

const listInvalidate = [
  { type: "StaffRealEstateRequests", id: "LIST" },
  { type: "StaffRealEstateSummary", id: "SUMMARY" },
  { type: "RealEstateRequests", id: "LIST" },
];

function detailInvalidate(id) {
  return [
    ...listInvalidate,
    { type: "StaffRealEstateRequest", id },
    { type: "RealEstateRequest", id },
    { type: "StaffRealEstateInspections", id: "LIST" },
  ];
}

export const staffRealEstateApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getStaffRealEstateSummary: builder.query({
      query: () => ({
        url: "/cooperatives/real-estate/dashboard/summary",
        method: "GET",
      }),
      transformResponse: (responseData) => mapStaffRealEstateSummary(responseData?.data),
      providesTags: [{ type: "StaffRealEstateSummary", id: "SUMMARY" }],
    }),

    listStaffRealEstateRequests: builder.query({
      query: (params = {}) => ({
        url: "/cooperatives/real-estate",
        method: "GET",
        params,
      }),
      transformResponse: transformListResponse,
      providesTags: (result) =>
        result?.items?.length
          ? [
              ...result.items.map((item) => ({
                type: "StaffRealEstateRequest",
                id: item.requestReference || item.id,
              })),
              { type: "StaffRealEstateRequests", id: "LIST" },
            ]
          : [{ type: "StaffRealEstateRequests", id: "LIST" }],
    }),

    getStaffRealEstateRequest: builder.query({
      query: (id) => ({
        url: `/cooperatives/real-estate/${encodeURIComponent(id)}`,
        method: "GET",
      }),
      transformResponse: (responseData) => mapStaffRealEstateDetail(responseData?.data),
      providesTags: (_r, _e, id) => [{ type: "StaffRealEstateRequest", id }],
    }),

    assignStaffRealEstateRequest: builder.mutation({
      query: ({ id, assigned_staff_id }) => ({
        url: `/cooperatives/real-estate/${encodeURIComponent(id)}/assign`,
        method: "POST",
        body: assigned_staff_id != null ? { assigned_staff_id } : {},
      }),
      transformResponse: (responseData) => mapStaffRealEstateDetail(responseData?.data),
      invalidatesTags: (_r, _e, { id }) => detailInvalidate(id),
    }),

    addStaffRealEstateNote: builder.mutation({
      query: ({ id, note }) => ({
        url: `/cooperatives/real-estate/${encodeURIComponent(id)}/notes`,
        method: "POST",
        body: { note },
      }),
      transformResponse: (responseData) => mapStaffRealEstateDetail(responseData?.data),
      invalidatesTags: (_r, _e, { id }) => detailInvalidate(id),
    }),

    staffRealEstateAction: builder.mutation({
      query: ({ id, action, body }) => ({
        url: `/cooperatives/real-estate/${encodeURIComponent(id)}/${action}`,
        method: "POST",
        body: body || {},
      }),
      transformResponse: (responseData) => mapStaffRealEstateDetail(responseData?.data),
      invalidatesTags: (_r, _e, { id }) => detailInvalidate(id),
    }),

    scheduleStaffRealEstateInspection: builder.mutation({
      query: ({ id, body }) => ({
        url: `/cooperatives/real-estate/${encodeURIComponent(id)}/schedule-inspection`,
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => mapStaffRealEstateDetail(responseData?.data),
      invalidatesTags: (_r, _e, { id }) => detailInvalidate(id),
    }),
  }),
});

export const {
  useGetStaffRealEstateSummaryQuery,
  useListStaffRealEstateRequestsQuery,
  useGetStaffRealEstateRequestQuery,
  useAssignStaffRealEstateRequestMutation,
  useAddStaffRealEstateNoteMutation,
  useStaffRealEstateActionMutation,
  useScheduleStaffRealEstateInspectionMutation,
} = staffRealEstateApi;

export const STAFF_REAL_ESTATE_ACTIONS = {
  markContacted: "mark-contacted",
  awaitingContact: "awaiting-contact",
  awaitingCustomer: "awaiting-customer",
  approve: "approve",
  complete: "complete",
  decline: "decline",
  cancel: "cancel",
  completeInspection: "complete-inspection",
  cancelInspection: "cancel-inspection",
  transition: "transition",
};
